/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { Component } from '@theme/component';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

/**
 * Floyd Friends loyalty blocks.
 *
 * State comes from the IMPACT loyalty app through the Shopify app proxy. That app calls
 * Voyado server-side, so nothing here holds a credential, and the customer is identified
 * by Shopify's signed `logged_in_customer_id` rather than by anything this file sends.
 *
 * The blocks share one request, so placing them on the same page costs a single round trip.
 *
 * That request is the expensive part: the app makes one Shopify call and four Voyado calls to
 * answer it, and the blocks sit in the header, so they are on every page. Two things keep it
 * from running on every page view. A definite answer is kept in sessionStorage for a few minutes,
 * and a block inside the account drawer only asks once the drawer is opened, since most visitors
 * never open it.
 */

const ENDPOINT = '/apps/loyalty/points';
const SIGNUP_ENDPOINT = '/apps/loyalty/signup';

// How long an answer is reused across pages. Short on purpose: points and vouchers change at the
// checkout and in Engage, and neither can reach this tab to say so.
const CACHE_TTL_MS = 5 * 60 * 1000;
const CACHE_KEY_PREFIX = 'loyalty:state:';

// Set by the loyalty app on the redirect back from Vipps, carrying the outcome of the join.
const VIPPS_RESULT_PARAM = 'loyalty_vipps';

// Only present when the join was anonymous: the verified address Vipps gave us, so the login
// form can be prefilled with it rather than inviting a second, unreconcilable contact.
const VIPPS_EMAIL_PARAM = 'loyalty_email';

// Backoff for the reads after an inconclusive first answer, waiting for the Engage app to stamp
// voyado.contactId on a customer who has just signed in for the first time. Observed taking
// longer than a single short retry allowed for, hence two attempts rather than one. Capped
// because the loading state is on screen throughout — past this we show the join prompt and
// let a refresh sort it out.
const RETRY_DELAYS_MS = [2000, 5000];

/**
 * @typedef {object} LoyaltyVoucher
 * @property {string} id
 * @property {string} [name]
 * @property {string | null} [expiresOn]
 * @property {{ currency: string, amount: number } | null} [value]
 */

/**
 * @typedef {object} LoyaltyState
 * @property {boolean} loggedIn
 * @property {boolean} [member]
 * @property {number} [points]
 * @property {LoyaltyVoucher[]} [vouchers]
 * @property {{ mobilePhone?: string | null }} [profile] What Voyado holds for this customer, for prefilling the join form.
 */

/** @type {Promise<LoyaltyState> | null} */
let inFlight = null;

/** @returns {Promise<LoyaltyState>} */
function requestLoyalty() {
  return fetch(ENDPOINT, { headers: { accept: 'application/json' } }).then((response) => {
    if (!response.ok) {
      const error = new Error(`Loyalty request failed with ${response.status}`);
      error.status = response.status;
      throw error;
    }
    return response.json().catch(() => {
      const error = new Error('Loyalty request returned invalid JSON');
      error.status = response.status;
      throw error;
    });
  });
}

/**
 * Resolves the loyalty state, retrying once when the first answer is inconclusive.
 *
 * The read immediately after account creation is expected to be: the Engage app has not
 * stamped `voyado.contactId` yet, so the app is resolving the contact by Shopify customer id,
 * which is unreliable in exactly that window. It answers `provisional` rather than guessing.
 * A single retry lands after the stamp, so a member who has just signed in sees their balance
 * instead of a join prompt — previously this took a manual refresh.
 */
async function resolveLoyalty() {
  try {
    const state = await requestLoyalty();
    if (!state?.provisional) return state;
  } catch (error) {
    // 502 is the app declining to guess about an unreachable Voyado. Worth one more ask;
    // anything else is a real failure and should surface immediately.
    if (error?.status !== 502) throw error;
  }

  let state;

  for (const delay of RETRY_DELAYS_MS) {
    await new Promise((resolve) => setTimeout(resolve, delay));

    try {
      state = await requestLoyalty();
      if (!state?.provisional) return state;
    } catch (error) {
      if (error?.status !== 502) throw error;
    }
  }

  // Still unresolved. Returning the last answer shows the join prompt, which is wrong for an
  // existing member but is the only honest thing left to say — and joining twice is a no-op.
  return state ?? requestLoyalty();
}

/**
 * The answer kept for this customer, if it is still fresh.
 *
 * Keyed by customer id, so a different person signing in on the same tab never sees the last
 * one's balance. Everything wrong with storage — blocked, full, unparseable — is a cache miss.
 *
 * @param {string | undefined} customerId
 * @returns {LoyaltyState | null}
 */
function readCachedState(customerId) {
  if (!customerId) return null;

  try {
    const raw = sessionStorage.getItem(CACHE_KEY_PREFIX + customerId);
    if (!raw) return null;

    const { at, state } = JSON.parse(raw);
    if (!state || Date.now() - at > CACHE_TTL_MS) {
      sessionStorage.removeItem(CACHE_KEY_PREFIX + customerId);
      return null;
    }

    return state;
  } catch {
    return null;
  }
}

/**
 * Keeps an answer that is final. A `provisional` one means the Engage app has not stamped the
 * contact yet, and keeping it would hold a new member on the join prompt for the whole TTL.
 * Whatever is kept for any other customer goes, so one tab never accumulates other people's data.
 *
 * @param {string | undefined} customerId
 * @param {LoyaltyState & { provisional?: boolean }} state
 */
function writeCachedState(customerId, state) {
  if (!customerId || !state?.loggedIn || state.provisional) return;

  try {
    clearCachedState();
    sessionStorage.setItem(CACHE_KEY_PREFIX + customerId, JSON.stringify({ at: Date.now(), state }));
  } catch {
    // Storage unavailable: the next page asks again, which is what it did before.
  }
}

/** Forgets every kept answer. Called when the customer joins, which changes what the answer is. */
function clearCachedState() {
  try {
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith(CACHE_KEY_PREFIX)) sessionStorage.removeItem(key);
    }
  } catch {
    // Nothing kept, nothing to forget.
  }
}

/** @param {string | undefined} customerId */
function loadLoyalty(customerId) {
  inFlight ??= resolveLoyalty().then((state) => {
    writeCachedState(customerId, state);

    return state;
  });

  return inFlight;
}

/**
 * Shared plumbing: resolves the loyalty state, publishes it as `data-state` for CSS to
 * switch on, and lets the subclass fill in the member view.
 *
 * @extends {Component<{}>}
 */
class LoyaltyBlock extends Component {
  /** The closed drawer this block is waiting on, if it is. @type {Element | null} */
  #drawer = null;

  connectedCallback() {
    super.connectedCallback();

    // Liquid already settled the state — no customer session, or a theme-editor preview
    // rendering sample values. Either way there is nothing to fetch.
    if (this.dataset.state !== 'loading') return;

    // A fresh answer from an earlier page needs no request at all, drawer open or not.
    const cached = readCachedState(this.dataset.customerId);
    if (cached) {
      try {
        this.#apply(cached);
        return;
      } catch (error) {
        console.warn('[loyalty] cached state could not be shown', error);
        clearCachedState();
      }
    }

    // In the account drawer and the drawer is shut: wait. A drawer restored open from the last
    // page is already [open] before this runs, so it falls through to the request below.
    const drawer = this.closest('theme-drawer');
    if (drawer && !drawer.hasAttribute('open')) {
      this.#drawer = drawer;
      drawer.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen, { once: true });
      return;
    }

    this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#drawer?.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);
  }

  #onDrawerOpen = () => {
    this.#drawer = null;
    this.#load();
  };

  async #load() {
    try {
      this.#apply(await loadLoyalty(this.dataset.customerId));
    } catch (error) {
      // Voyado unreachable, or the app returned 502. An absent block beats a wrong
      // balance, so the error state deliberately renders nothing.
      console.warn('[loyalty]', error);
      this.dataset.state = 'error';
    }
  }

  /** @param {LoyaltyState} state */
  #apply(state) {
    if (!state.loggedIn) {
      this.dataset.state = 'anonymous';
    } else if (!state.member) {
      this.renderNonMember(state);
      this.dataset.state = 'non-member';
    } else {
      this.renderMember(state);
      this.dataset.state = 'member';
    }
  }

  /**
   * @param {LoyaltyState} _state
   */
  renderMember(_state) {}

  /**
   * @param {LoyaltyState} _state
   */
  renderNonMember(_state) {}

  /** Locale for number and date formatting, from the shop's active locale. */
  get locale() {
    return this.dataset.locale || undefined;
  }
}

/** Renders the points balance and the progress to the next voucher. */
class LoyaltyPoints extends LoyaltyBlock {
  requiredRefs = ['points'];

  /** @param {LoyaltyState} state */
  renderMember(state) {
    const points = state.points ?? 0;

    this.refs.points.textContent = new Intl.NumberFormat(this.locale).format(points);
    this.#renderProgress(points);
  }

  /**
   * Shows how far off the next bonussjekk is, as a bar and the sentence explaining it.
   *
   * Shown at any balance, zero included — a new member is a full voucher away and saying so is
   * the point of the line. Points past a whole voucher count toward the next one, so 2 300 of
   * 1 000 shows 700 remaining rather than a negative number, and a balance landing exactly on
   * a multiple shows a full 1 000 again, with the bar back at empty to match.
   *
   * Renders nothing when the threshold or the message is missing, rather than a bar with no
   * explanation next to it.
   *
   * @param {number} points
   */
  #renderProgress(points) {
    const perVoucher = Number(this.dataset.pointsPerVoucher);
    const template = this.dataset.progressText ?? '';

    if (!perVoucher || perVoucher <= 0 || !template || !this.refs.progressText) {
      this.dataset.progress = 'false';
      return;
    }

    const earned = points % perVoucher;
    const remaining = perVoucher - earned;

    this.refs.progressText.textContent = template
      .replace('{points}', new Intl.NumberFormat(this.locale).format(remaining))
      .replace('{value}', this.dataset.voucherValue ?? '');

    if (this.refs.progressBar) {
      this.refs.progressBar.style.width = `${(earned / perVoucher) * 100}%`;
    }

    this.dataset.progress = 'true';
  }
}

/**
 * Renders everything a member can spend — reward vouchers and assigned promotions — as one
 * list.
 *
 * They arrive from Voyado as two collections and were once two blocks, but to a shopper they
 * are one thing: money off, waiting to be used. Nothing distinguishes them on the card, and
 * vouchers lead because they are the ones a member earned.
 */
class LoyaltyVouchers extends LoyaltyBlock {
  requiredRefs = ['list'];

  /** @param {LoyaltyState} state */
  renderMember(state) {
    const items = [
      ...(state.vouchers ?? []).map((voucher) => this.#voucherItem(voucher)),
      ...(state.promotions ?? []).map((promotion) => this.#promotionItem(promotion)),
    ];

    // A member with nothing to spend is an ordinary state, not an error — CSS swaps in the
    // empty copy on this attribute.
    this.dataset.empty = items.length === 0 ? 'true' : 'false';
    this.refs.list.replaceChildren(...items);
  }

  /**
   * One card, whatever it was built from: a headline, the line naming what it is, and the
   * standing lines about when and where it can be used.
   *
   * @param {string} headline
   * @param {string | null | undefined} expiresOn
   * @param {string | undefined} label What to call it — a voucher and a promotion differ
   *   only here.
   * @returns {HTMLLIElement}
   */
  #card(headline, expiresOn, label) {
    const item = document.createElement('li');
    item.className = 'loyalty-voucher';

    const info = document.createElement('div');
    info.className = 'loyalty-voucher__info';
    info.append(this.#span('loyalty-voucher__value', headline));

    if (label) info.append(this.#span('loyalty-voucher__name', label));

    const expires = this.#formatDate(expiresOn);
    if (expires) {
      const prefix = this.dataset.expiresLabel ?? '';
      info.append(this.#span('loyalty-voucher__expires', `${prefix} ${expires}`.trim()));
    }

    // The same for every card — where it is redeemed is a property of how Floyd Friends
    // works, not of the individual voucher, so it is copy rather than payload.
    const note = this.dataset.checkoutNote;
    if (note) info.append(this.#span('loyalty-voucher__note', note));

    item.append(info);

    // Everything reaching this list is usable — the app filters redeemed and expired vouchers
    // out server-side — so the badge is a constant label rather than a status to compute.
    const status = this.dataset.statusLabel;
    if (status) item.append(this.#span('loyalty-voucher__status', status));

    return item;
  }

  /**
   * A promotion's headline is what a merchant wrote for shoppers. Its code is deliberately
   * not shown: see the block's own documentation.
   *
   * @param {Record<string, any>} promotion
   * @returns {HTMLLIElement}
   */
  #promotionItem(promotion) {
    return this.#card(
      promotion.heading || promotion.description || '',
      promotion.expiresOn,
      this.dataset.promotionLabel,
    );
  }

  /**
   * @param {LoyaltyVoucher} voucher
   * @returns {HTMLLIElement}
   */
  #voucherItem(voucher) {
    const percentage = this.#percentage(voucher.value);

    // A voucher's upstream name is the Engage job that minted it — "Generate reward vouchers
    // 26/08/2026 14:00" — which means nothing to a shopper, so the amount is the headline.
    const value = percentage === null ? this.#formatValue(voucher.value) : `${percentage}%`;

    return this.#card(value ?? '', voucher.expiresOn, this.dataset.voucherLabel);
  }

  /**
   * The percentage a voucher takes off, or null when it carries a money value instead.
   *
   * Every reward voucher seen on the staging tenant is monetary — `{ amount, currency }` — and
   * the payload has no field distinguishing the two, so this reads the shapes a percentage
   * could plausibly arrive in rather than one confirmed against real data. If percentage
   * offers turn out to be Engage *promotions* rather than reward vouchers, they will not reach
   * this code at all and this can go.
   *
   * @param {LoyaltyVoucher['value']} value
   * @returns {number | null}
   */
  #percentage(value) {
    if (!value) return null;
    if (typeof value.percentage === 'number') return value.percentage;

    const type = String(value.type ?? value.valueType ?? '');
    if (/percent/i.test(type) && typeof value.amount === 'number') return value.amount;

    // A bare amount with no currency is the remaining way a percentage could present.
    if (typeof value.amount === 'number' && !value.currency) return value.amount;

    return null;
  }

  /**
   * textContent throughout — voucher names are external data and must never be parsed as markup.
   *
   * @param {string} className
   * @param {string} text
   */
  #span(className, text) {
    const span = document.createElement('span');
    span.className = className;
    span.textContent = text;
    return span;
  }

  /** @param {LoyaltyVoucher['value']} value */
  #formatValue(value) {
    if (!value || typeof value.amount !== 'number' || !value.currency) return null;

    const { moneyFormat, currencyCode } = this.dataset;

    // Voyado sends major units; the shop money format keeps vouchers identical to the Liquid `money` filter.
    if (moneyFormat && value.currency === currencyCode) {
      const minorUnits = convertMoneyToMinorUnits(String(value.amount), value.currency);
      if (minorUnits !== null) return formatMoney(minorUnits, moneyFormat, value.currency);
    }

    try {
      return new Intl.NumberFormat(this.locale, { style: 'currency', currency: value.currency }).format(value.amount);
    } catch {
      // Unknown currency code — show the raw pair rather than nothing.
      return `${value.amount} ${value.currency}`;
    }
  }

  /** @param {string | null | undefined} iso */
  #formatDate(iso) {
    if (!iso) return null;

    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;

    return new Intl.DateTimeFormat(this.locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
  }
}

/**
 * The outcome of a Vipps signup, taken from the URL the loyalty app redirects back to.
 *
 * Read once at module load rather than per instance: the drawer and the page can each hold a
 * signup block and both need the answer, but the parameter should only be stripped once.
 *
 * @returns {string | null} A `data-state` value, or null when this is not a return from Vipps.
 */
function readVippsOutcome() {
  const params = new URLSearchParams(window.location.search);
  const outcome = params.get(VIPPS_RESULT_PARAM);
  if (!outcome) return null;

  vippsEmail = params.get(VIPPS_EMAIL_PARAM);

  // Strip them, so a refresh, a back navigation or a shared link cannot replay the confirmation
  // — and so an email address does not sit in the URL to be shared or logged.
  params.delete(VIPPS_RESULT_PARAM);
  params.delete(VIPPS_EMAIL_PARAM);
  const query = params.toString();
  history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);

  // Same vocabulary the signup endpoint returns, so both routes into membership land on the
  // same copy rather than growing a parallel set of messages.
  switch (outcome) {
    case 'joined':
      return 'joined';
    case 'joined_anonymous':
      return 'joined-anonymous';
    case 'under_age':
      return 'under-age';
    case 'no_contact':
      return 'not-ready';
    case 'phone_taken':
      return 'phone-taken';
    case 'invalid_phone':
      return 'invalid-phone';
    case 'ambiguous_email':
      return 'ambiguous-email';
    default:
      return 'error';
  }
}

/** @type {string | null} */
let vippsEmail = null;
const vippsOutcome = readVippsOutcome();

// Coming back from Vipps means the membership just changed, so whatever was kept is out of date.
if (vippsOutcome) clearCachedState();

/**
 * The join form.
 *
 * Membership comes from the app, not from the customer metafield: it is a consent, and the app
 * reads `clubMember` from the Engage core API. The metafield exists for customer segments and
 * automatic discounts, which cannot call out to Voyado at all.
 *
 * @extends {LoyaltyBlock}
 */
class LoyaltySignup extends LoyaltyBlock {
  requiredRefs = ['form'];

  connectedCallback() {
    // Returning from Vipps: the app has already written the membership, so publish the outcome
    // before the base class reads data-state. It only fetches while 'loading', so this skips a
    // pointless membership read and avoids flashing the join form before the confirmation.
    if (vippsOutcome) this.dataset.state = this.#resolveTaken(vippsOutcome);

    // Resolves membership and sets data-state to member / non-member / anonymous.
    super.connectedCallback();
    this.refs.form.addEventListener('submit', this.#submit);
    this.refs.phone?.addEventListener('input', this.#markPhoneTyped, { once: true });

    // After super, not before: the base class is what populates `refs`, and prefilling the
    // login link needs one. Setting the state has to happen first, though, so the base class
    // skips its membership read — hence the two being split rather than done together.
    if (vippsOutcome === 'joined-anonymous') this.#prefillLogin(vippsEmail ?? '');

    if (vippsOutcome) this.#revealInDrawer();
  }

  /**
   * Puts the number Voyado already holds in the phone field.
   *
   * Engage matches store contacts on this number, so it is the one that counts, and it replaces
   * whatever the Shopify account had — which is often nothing, for a customer who first showed up
   * as a guest purchase. Anything the shopper has already typed is left alone.
   *
   * @param {LoyaltyState} state
   */
  renderNonMember(state) {
    const phone = state.profile?.mobilePhone;
    const input = this.refs.phone;

    if (phone && input && !input.dataset.typed) input.value = phone;
  }

  #markPhoneTyped = () => {
    if (this.refs.phone) this.refs.phone.dataset.typed = 'true';
  };

  /**
   * Picks the state for a number another contact already holds.
   *
   * Signed out, signing in is the way forward. Signed in, the shopper already is, and under a
   * different account, so a login button leads nowhere: the form comes back with a message under
   * the number instead, so they can use another.
   *
   * @param {string} state
   * @returns {string}
   */
  #resolveTaken(state) {
    return state === 'phone-taken' && this.dataset.customerId ? 'phone-in-use' : state;
  }

  /**
   * Confirms an anonymous join and points them at the login form with the address they gave.
   *
   * Prefilling matters more than convenience here: signing in under a different address creates
   * a second Voyado contact, and nothing reconciles the two. `login_hint` only survives on the
   * shop-scoped account URL — /account/login drops it — so the href is rewritten rather than
   * having the parameter appended to the Liquid default.
   *
   * @param {string} email
   */
  #showJoinedAnonymous(email) {
    this.#prefillLogin(email);
    this.dataset.state = 'joined-anonymous';
  }

  /**
   * Points the login link at the address they just gave us.
   *
   * `login_hint` only survives on the shop-scoped account URL — /account/login strips it — so
   * the href is rewritten rather than having a parameter appended to the Liquid default.
   * Verified against the demo store 2026-09-08.
   *
   * @param {string} email
   */
  #prefillLogin(email) {
    const link = this.refs.loginLink;
    const base = link?.dataset.loginBase;

    if (!link || !base || !email) return;

    link.href = `${base}?login_hint=${encodeURIComponent(email)}`;
  }

  /**
   * Opens the account drawer at the view this block sits in.
   *
   * Vipps returns through a full page load with every drawer shut, and the theme only restores
   * open drawers on wide viewports — the opposite of where this traffic is, since the flow
   * needs the Vipps app on a phone. Without this the outcome renders where nobody sees it.
   *
   * Opening is not enough on the signed-out panel: that has two views, and the checkbox driving
   * them resets on page load, so the drawer would reopen on the benefits view with this block
   * hidden behind it. Whatever we have to say — joined, or an error — has to be on screen.
   */
  #revealInDrawer() {
    const drawer = this.closest('theme-drawer');
    if (!drawer) return;

    // Only when this block is in the view the switch reveals; the signed-in panel has no switch.
    if (this.closest('.account-drawer__view--signup')) {
      const switcher = drawer.querySelector('.account-drawer__view-switch');
      if (switcher instanceof HTMLInputElement) switcher.checked = true;
    }

    // The block can upgrade before the drawer does; open() is a no-op when already showing.
    customElements.whenDefined('theme-drawer').then(() => drawer.open());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.form?.removeEventListener('submit', this.#submit);
  }

  /** @param {SubmitEvent} event */
  #submit = async (event) => {
    event.preventDefault();

    // Joining twice is a no-op in Engage, but a double-click should still not fire twice.
    if (this.dataset.state === 'submitting') return;
    this.dataset.state = 'submitting';

    try {
      const body = new FormData(this.refs.form);

      // Send the form itself: the app allow-lists and validates the fields it accepts.
      const response = await fetch(SIGNUP_ENDPOINT, {
        method: 'POST',
        headers: { accept: 'application/json' },
        body,
      });
      const result = await response.json().catch(() => ({}));
      const reason = result.error || result.reason;

      if (!response.ok || !result.ok) throw new Error(reason || `HTTP ${response.status}`);

      // The answer kept for this customer says non-member, and it no longer does.
      clearCachedState();

      // Joined with no Shopify session: real in Voyado, invisible to Shopify until they sign in.
      if (result.anonymous) {
        this.#showJoinedAnonymous(String(body.get('email') ?? ''));
        return;
      }

      this.dataset.state = 'joined';
    } catch (error) {
      // The shopper took an action, so unlike the read-only blocks this one owes them a
      // visible failure rather than silence.
      console.warn('[loyalty] signup failed', error);

      // `no_contact` means Voyado has not synced this customer yet — common in the first
      // moments after registering. Retrying shortly works, so it gets its own copy rather
      // than reading as a breakage.
      const reason = error instanceof Error ? error.message : '';

      // Each of these is something the shopper can act on, so they get their own copy rather
      // than the catch-all failure.
      if (reason === 'no_contact') this.dataset.state = 'not-ready';
      else if (reason === 'under_age') this.dataset.state = 'under-age';
      // Another contact already holds this number. Retrying cannot help, so the copy asks
      // for a different one rather than telling them to wait.
      else if (reason === 'phone_taken') {
        this.dataset.state = this.#resolveTaken('phone-taken');
        if (this.dataset.state === 'phone-in-use') this.refs.phone?.focus();
      }
      // Several contacts share the address. Only support can untangle that, so no retry is offered.
      else if (reason === 'ambiguous_email') this.dataset.state = 'ambiguous-email';
      // The number cannot be read. The form comes back with a message under it and the cursor in
      // the field, since correcting it is all that is needed.
      else if (reason === 'invalid_phone') {
        this.dataset.state = 'invalid-phone';
        this.refs.phone?.focus();
      } else this.dataset.state = 'error';
    }
  };
}

if (!customElements.get('loyalty-points')) customElements.define('loyalty-points', LoyaltyPoints);
if (!customElements.get('loyalty-vouchers')) customElements.define('loyalty-vouchers', LoyaltyVouchers);
if (!customElements.get('loyalty-signup')) customElements.define('loyalty-signup', LoyaltySignup);
